'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useApiClient } from '../contexts/ApiContext';
import { useCentrifugo } from '../contexts/CentrifugoContext';
import { queryKeys } from '../lib/queryClient';
import type { OpenGame, LiveGame } from '../services/generated/Api';

/** The open (FORMING) games list — polled every 30s and invalidated live by Centrifugo.
 *  `refetchOnMount: 'always'` — the cached list paints instantly on every return to the lobby
 *  while a fresh one lands behind it, regardless of staleness. `refetchOnWindowFocus: 'always'`
 *  for the same reason on resume: a phone that spent a minute in the background comes back to
 *  a list where lobbies have long since filled, and the socket only replays events it caught. */
export function useOpenGames(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rushMidOpenGames,
    queryFn: () => apiClient.api.rushMidControllerListOpenGames(),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    enabled,
  });
}

/** The live (PLAYING) games list — the «Идут сейчас» spectate section. Polled every 30s and
 *  invalidated live by Centrifugo (a match entering play / finishing fires a games event). */
export function useLiveGames(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rushMidLiveGames,
    queryFn: () => apiClient.api.rushMidControllerListLiveGames(),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnMount: 'always',
    enabled,
  });
}

/** One open game by id (404s once it fills / starts / closes). `null` data is the live
 *  «it's gone» verdict written by the games subscription when a start/close event names this
 *  match — knowing the game left FORMING, re-asking for it could only 404, so the poll stops
 *  on `null` exactly as it stops on an error. */
export function useOpenGame(matchId: string | null, enabled = true) {
  const apiClient = useApiClient();

  return useQuery<OpenGame | null>({
    queryKey: queryKeys.rushMidOpenGame(matchId ?? ''),
    queryFn: () => apiClient.api.rushMidControllerGetOpenGame(matchId ?? ''),
    enabled: enabled && !!matchId,
    staleTime: 5 * 1000,
    refetchInterval: (query) =>
      query.state.status === 'error' || query.state.data === null ? false : 5 * 1000,
    // the interval is not the only thing that would re-ask: a gone game must not be refetched
    // on focus either, or tabbing back turns the known verdict into a 404
    refetchOnWindowFocus: (query) => query.state.data !== null,
    retry: false,
  });
}

const gamesChannel = (prefix: string): string => (prefix ? `${prefix}_rush_mid_games` : 'rush_mid_games');

/** `joinable`/`joinableAt` are the caller's own verdict and only REST carries them — the
 *  broadcast payload is one message for every viewer. Carry the last known verdict across a
 *  broadcast upsert so a seat change doesn't silently re-enable a lobby the grace fences off;
 *  it expires on its own at `joinableAt`, and the next list refetch re-decides. */
const mergeJoinGate = (prev: OpenGame, next: OpenGame): OpenGame => {
  if (next.joinable !== undefined || prev.joinable === undefined) return next;
  return {
    ...next,
    joinable: prev.joinable,
    joinBlockedReason: prev.joinBlockedReason,
    joinableAt: prev.joinableAt,
  };
};

/** True while the newbie grace still fences this lobby off for us. The window closes by the
 *  clock alone, so a card re-enables itself without a refetch once `joinableAt` passes. */
export const isJoinBlocked = (game: OpenGame, now = Date.now()): boolean =>
  game.joinable === false && (!game.joinableAt || now < new Date(game.joinableAt).getTime());

export type RushMidGamesEventType =
  | 'game_created'
  | 'game_joined'
  | 'game_left'
  | 'game_started'
  | 'game_playing'
  | 'game_closed'
  | 'game_finished';

export interface RushMidGamesEvent {
  type: RushMidGamesEventType;
  matchId?: string;
  /** upsert payload for the open list (created / joined / left) */
  open?: OpenGame;
  /** upsert payload for the «live now» list (playing) */
  live?: LiveGame;
}

/**
 * Live invalidation for the open-games surface — mirrors {@link useCaseBattleSubscription}. Any
 * event refreshes BOTH lists (open + «Идут сейчас») — a game_started moves a match from open to
 * live, a game_finished drops it off live; an event carrying a matchId also refreshes that single
 * game (so an opened joiner view repaints its slot grid, and a started/closed game 404s the viewer out).
 */
export function useRushMidGamesSubscription() {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();

  const handleEvent = useCallback(
    (data: unknown) => {
      const event = data as RushMidGamesEvent;
      if (!event || typeof event.type !== 'string') return;

      // Patch the cached list in place instead of refetching the whole thing — only the changed
      // card mounts/unmounts, so the list doesn't churn (and the backend isn't hammered by every
      // lobby refetching on every event). A missing payload (older backend) falls back to refetch.
      const patch = <T extends { matchId: string }>(
        key: readonly unknown[],
        fn: (games: T[]) => T[],
      ) => {
        const cur = queryClient.getQueryData<{ games: T[] }>(key);
        if (!cur) { queryClient.invalidateQueries({ queryKey: key }); return; }
        queryClient.setQueryData(key, { ...cur, games: fn(cur.games) });
      };
      const upsert = <T extends { matchId: string }>(games: T[], g: T): T[] => {
        const i = games.findIndex((x) => x.matchId === g.matchId);
        if (i === -1) return [...games, g];
        const next = games.slice();
        next[i] = g;
        return next;
      };
      const upsertOpen = (games: OpenGame[], g: OpenGame): OpenGame[] => {
        const i = games.findIndex((x) => x.matchId === g.matchId);
        if (i === -1) return [...games, g];
        const next = games.slice();
        next[i] = mergeJoinGate(games[i], g);
        return next;
      };
      const drop = <T extends { matchId: string }>(games: T[], id: string): T[] =>
        games.filter((x) => x.matchId !== id);

      switch (event.type) {
        case 'game_created':
        case 'game_joined':
        case 'game_left':
          if (event.open) patch<OpenGame>(queryKeys.rushMidOpenGames, (g) => upsertOpen(g, event.open!));
          else queryClient.invalidateQueries({ queryKey: queryKeys.rushMidOpenGames });
          break;
        case 'game_started':
        case 'game_closed':
          if (event.matchId) patch<OpenGame>(queryKeys.rushMidOpenGames, (g) => drop(g, event.matchId!));
          break;
        case 'game_playing':
          if (event.live) patch<LiveGame>(queryKeys.rushMidLiveGames, (g) => upsert(g, event.live!));
          else queryClient.invalidateQueries({ queryKey: queryKeys.rushMidLiveGames });
          break;
        case 'game_finished':
          if (event.matchId) patch<LiveGame>(queryKeys.rushMidLiveGames, (g) => drop(g, event.matchId!));
          break;
      }

      if (event.matchId) {
        const single = queryKeys.rushMidOpenGame(event.matchId);
        if (event.type === 'game_started' || event.type === 'game_closed') {
          // We already know it left FORMING, so a refetch could only come back 404. Write the
          // «gone» verdict straight into the cache: an opened joiner switches to its gone state
          // without the round trip, and its poll stops.
          queryClient.setQueryData(single, null);
        } else {
          // seat change — repaint the opened joiner's slot grid
          queryClient.invalidateQueries({ queryKey: single });
        }
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (!isConnected) return;
    const channel = gamesChannel(channelPrefix);
    return subscribe(channel, handleEvent);
  }, [isConnected, channelPrefix, subscribe, handleEvent]);

  // Events that land while the socket is down are gone for good — the lobby list would keep
  // showing games that filled minutes ago until the 30s poll happened to fire. Resync both
  // lists on every (re)connect, which is also the first thing a resumed app does.
  useEffect(() => {
    if (!isConnected) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.rushMidOpenGames });
    queryClient.invalidateQueries({ queryKey: queryKeys.rushMidLiveGames });
  }, [isConnected, queryClient]);

  return { isConnected };
}
